import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { GameResult } from "@minigames/shared";
import { Board } from "../components/Board.js";
import { CheckersBoard } from "../components/CheckersBoard.js";
import { GomokuBoard, ReversiBoard } from "../components/CellBoard.js";
import { DurakTable } from "../components/DurakTable.js";
import { SeaBattle } from "../components/SeaBattle.js";
import { ChatPanel } from "../components/ChatPanel.js";
import { FirstHint, RulesSheet } from "../components/RulesSheet.js";
import { PlayerBar } from "../components/PlayerBar.js";
import { socket } from "../lib/socket.js";
import { useStore } from "../lib/store.js";
import { haptic } from "../lib/telegram.js";
import { sound } from "../lib/sound.js";
import { hasSeenGame, markGameSeen } from "../lib/session.js";
import type {
  CheckersView,
  ChessView,
  DurakMove,
  DurakView,
  GomokuView,
  ReversiView,
  SeaView,
} from "../lib/views.js";

export function Game() {
  const { snapshot, me, drawOfferedBy, chat, reactions, reset, setDrawOffer } = useStore();
  const [confirmResign, setConfirmResign] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  // Подсказку поверх доски показываем только при первом заходе в игру.
  const [showHint, setShowHint] = useState(() =>
    snapshot ? !hasSeenGame(snapshot.game) : false,
  );
  /** Момент получения снимка в наших часах — база для интерполяции часов. */
  const clockSince = useRef<number>(performance.now());
  const lastPly = useRef<number>(-1);

  useEffect(() => {
    if (!snapshot) return;
    clockSince.current = performance.now();
    if (snapshot.ply !== lastPly.current) {
      const first = lastPly.current === -1;
      lastPly.current = snapshot.ply;
      if (snapshot.ply > 0 && !first) {
        haptic.move();
        if (snapshot.game === "durak") sound.card();
        else sound.move();
      }
    }
  }, [snapshot]);

  const finished = snapshot?.status === "finished";
  useEffect(() => {
    if (!finished || !snapshot?.result || !me) return;
    const r = snapshot.result;
    if (r.kind === "draw") {
      haptic.warn();
      sound.draw();
    } else if (r.winner === me.id) {
      haptic.win();
      sound.win();
    } else {
      haptic.lose();
      sound.lose();
    }
  }, [finished, snapshot?.result, me]);

  const clocks = useMemo(() => {
    if (!snapshot?.clock || !me) return null;
    const opp = snapshot.players.opponent?.id;
    return {
      mine: snapshot.clock.remaining[me.id] ?? 0,
      theirs: opp ? (snapshot.clock.remaining[opp] ?? 0) : 0,
      turnPlayer: snapshot.clock.turnPlayer,
    };
  }, [snapshot, me]);

  if (!snapshot || !me || snapshot.view === null) return null;

  const game = snapshot.game;

  /**
   * Выход в меню. Обязательно отписываемся от комнаты по сокету: иначе при
   * следующем переподключении клиент снова войдёт в ту же партию и сервер
   * пришлёт её снимок — игрока вернёт на экран законченной игры.
   */
  const leave = () => {
    haptic.tap();
    socket.leaveMatch(snapshot.matchId);
    reset();
  };

  const send = (move: unknown) => {
    socket.send({ t: "move", matchId: snapshot.matchId, ply: snapshot.ply, move });
  };

  // Все представления несут `turn` — этого достаточно, чтобы понять чей ход,
  // не разбирая каждую игру отдельно.
  const turn = (snapshot.view as { turn: string | null }).turn;
  const myTurn = snapshot.status === "active" && turn === me.id;

  return (
    <div className="stack" style={{ gap: 10 }}>
      <PlayerBar
        player={snapshot.players.opponent}
        reaction={
          snapshot.players.opponent
            ? (reactions[snapshot.players.opponent.id]?.emoji ?? null)
            : null
        }
        remainingMs={clocks?.theirs ?? null}
        running={Boolean(
          snapshot.status === "active" &&
            clocks &&
            clocks.turnPlayer === snapshot.players.opponent?.id,
        )}
        since={clockSince.current}
      />

      <AnimatePresence>
        {showHint && (
          <FirstHint
            key="hint"
            game={game}
            onDismiss={() => {
              markGameSeen(game);
              setShowHint(false);
            }}
          />
        )}
      </AnimatePresence>

      {game === "chess" && (
        <Board
          view={snapshot.view as ChessView}
          interactive={myTurn}
          onMove={(from, to, promotion) => send({ from, to, ...(promotion ? { promotion } : {}) })}
        />
      )}
      {game === "durak" && (
        <DurakTable view={snapshot.view as DurakView} onMove={(m: DurakMove) => send(m)} />
      )}
      {game === "checkers" && (
        <CheckersBoard view={snapshot.view as CheckersView} onMove={send} />
      )}
      {game === "reversi" && (
        <ReversiBoard view={snapshot.view as ReversiView} onMove={send} />
      )}
      {game === "gomoku" && (
        <GomokuBoard view={snapshot.view as GomokuView} onMove={send} />
      )}
      {game === "seabattle" && (
        <SeaBattle view={snapshot.view as SeaView} onMove={send} />
      )}

      <PlayerBar
        player={snapshot.players.me}
        reaction={reactions[me.id]?.emoji ?? null}
        remainingMs={clocks?.mine ?? null}
        running={Boolean(snapshot.status === "active" && clocks && clocks.turnPlayer === me.id)}
        since={clockSince.current}
      />

      {snapshot.players.opponent && (
        <ChatPanel
          messages={chat}
          meId={me.id}
          opponentName={
            snapshot.players.opponent.firstName ??
            snapshot.players.opponent.username ??
            "Соперник"
          }
          onSend={(text) => socket.send({ t: "chat", matchId: snapshot.matchId, text })}
          onReact={(emoji) => socket.send({ t: "reaction", matchId: snapshot.matchId, emoji })}
        />
      )}

      {snapshot.status === "active" && (
        <div className="row" style={{ gap: 8 }}>
          {game !== "durak" && (
            <button
              className="btn"
              onClick={() => {
                haptic.tap();
                socket.send({ t: "draw:offer", matchId: snapshot.matchId });
              }}
              disabled={snapshot.drawOfferBy === me.id}
            >
              {snapshot.drawOfferBy === me.id ? "Ничья предложена" : "Ничья"}
            </button>
          )}
          <button
            className="btn"
            style={{ width: "auto", flex: "none", padding: "12px 14px" }}
            onClick={() => {
              haptic.tap();
              setRulesOpen(true);
            }}
            title="Правила"
          >
            ?
          </button>
          <button className="btn btn-danger" onClick={() => setConfirmResign(true)}>
            Сдаться
          </button>
        </div>
      )}

      <RulesSheet game={game} open={rulesOpen} onClose={() => setRulesOpen(false)} />

      <AnimatePresence>
        {drawOfferedBy && snapshot.status === "active" && (
          <Sheet key="draw">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Соперник предлагает ничью</div>
            <div className="muted small" style={{ marginBottom: 14 }}>
              Согласиться — партия закончится вничью
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn"
                onClick={() => {
                  haptic.tap();
                  socket.send({ t: "draw:decline", matchId: snapshot.matchId });
                  setDrawOffer(null);
                }}
              >
                Отказаться
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  haptic.tap();
                  socket.send({ t: "draw:accept", matchId: snapshot.matchId });
                }}
              >
                Согласиться
              </button>
            </div>
          </Sheet>
        )}

        {confirmResign && (
          <Sheet key="resign">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Сдаться?</div>
            <div className="muted small" style={{ marginBottom: 14 }}>
              Партия будет засчитана как поражение
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn" onClick={() => setConfirmResign(false)}>
                Играть дальше
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  haptic.tap();
                  socket.send({ t: "resign", matchId: snapshot.matchId });
                  setConfirmResign(false);
                }}
              >
                Сдаться
              </button>
            </div>
          </Sheet>
        )}

        {finished && snapshot.result && (
          <Sheet key="end">
            <Outcome result={snapshot.result} meId={me.id} />
            {snapshot.ratingDelta?.[me.id] && (
              <div style={{ textAlign: "center", marginTop: 10 }}>
                <span
                  className={`rating-delta ${
                    snapshot.ratingDelta[me.id]!.delta >= 0 ? "up" : "down"
                  }`}
                >
                  {snapshot.ratingDelta[me.id]!.delta >= 0 ? "+" : ""}
                  {snapshot.ratingDelta[me.id]!.delta} рейтинга
                </span>
                <div className="muted small" style={{ marginTop: 4 }}>
                  Теперь {snapshot.ratingDelta[me.id]!.after}
                </div>
              </div>
            )}
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={leave}>
              В меню
            </button>
            {snapshot.seed && (
              <div className="muted small" style={{ marginTop: 12, textAlign: "center" }}>
                Раздача честная — сид раскрыт
              </div>
            )}
          </Sheet>
        )}
      </AnimatePresence>
    </div>
  );
}

function Sheet({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        padding: "20px 16px calc(var(--safe-bottom) + 20px)",
        background: "var(--bg-elev)",
        borderTop: "1px solid var(--line)",
        borderRadius: "20px 20px 0 0",
        boxShadow: "0 -12px 40px rgba(0,0,0,.5)",
        zIndex: 100,
      }}
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", stiffness: 380, damping: 34 }}
    >
      {children}
    </motion.div>
  );
}

function Outcome({ result, meId }: { result: GameResult; meId: string }) {
  const REASONS: Record<string, string> = {
    checkmate: "мат",
    resignation: "сдача",
    timeout: "время вышло",
    stalemate: "пат",
    agreement: "по согласию",
    insufficient_material: "недостаточно материала",
    threefold_repetition: "троекратное повторение",
    fifty_moves: "правило 50 ходов",
    timeout_vs_insufficient_material: "время вышло, но матовать нечем",
    durak: "остался с картами",
    both_out: "оба вышли",
    five_in_a_row: "пять в ряд",
    board_full: "доска заполнена",
    more_discs: "больше фишек",
    equal_discs: "поровну фишек",
    no_pieces: "не осталось шашек",
    no_moves: "нечем ходить",
    fleet_destroyed: "флот уничтожен",
    quiet_moves_limit: "долго без взятий",
  };
  const reason = REASONS[result.reason] ?? result.reason;

  if (result.kind === "draw") {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 40 }}>🤝</div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Ничья</div>
        <div className="muted small">{reason}</div>
      </div>
    );
  }
  const won = result.winner === meId;
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 40 }}>{won ? "🏆" : "🃏"}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{won ? "Победа" : "Поражение"}</div>
      <div className="muted small">{reason}</div>
    </div>
  );
}
